import type { Identifier } from "ra-core";

import { formatoFecha, formatoFechaHora } from "../negocios/formato";
import { claveCanal, claveResultadoPorCanal } from "./catalogos";
import { claveEtapaOFase } from "./etapas";
import type { FichaProspect, Negocio, Nota } from "./tipos";

/**
 * «Abrir SDR» (Jan y Robert, 13/10/2026): cuando una ficha está en «Consultoría agendada», el
 * botón copia al portapapeles un texto para abrir el chat «SDR - [Contacto] - [Negocio]» con todo
 * lo que hace falta para preparar la consultoría: negocio, contacto, teléfonos, email, web y
 * Maps, lo que manda Prospect (resumen y análisis), las notas de las llamadas, la consultoría
 * (fecha, hora y quién la agendó) y las dos tareas. Solo lee lo que la app ya tiene.
 */

type Traductor = (clave: string, opciones?: Record<string, unknown>) => string;

export type DatosSdr = {
  negocio: Negocio;
  notas: Nota[];
  /** Nombre de un usuario por su id (vacío si no se conoce). */
  nombreUsuario: (id?: Identifier | null) => string;
  t: Traductor;
};

const limpio = (s?: string | null) => (s ?? "").replace(/\s+/g, " ").trim();

/** La persona de contacto: la de la ficha o, si no hay, el dueño que manda Prospect. */
export const contactoSdr = (negocio: Negocio): string => {
  const ficha = negocio.prospect_ficha as FichaProspect | null | undefined;
  return limpio(negocio.persona_contacto) || limpio(ficha?.dueno?.nombre);
};

export const tituloSdr = (negocio: Negocio, t: Traductor): string =>
  `SDR - ${contactoSdr(negocio) || t("nevora.sdr.sin_contacto")} - ${limpio(negocio.nombre)}`;

/** Teléfonos sin repetir: el de la ficha y los de Prospect, con su tipo y de quién son. */
const telefonos = (
  negocio: Negocio,
  ficha?: FichaProspect | null,
): string[] => {
  const vistos = new Set<string>();
  const res: string[] = [];
  const clave = (n: string) => n.replace(/\D/g, "").slice(-9);
  for (const tel of ficha?.telefonos ?? []) {
    const n = limpio(tel.numero);
    if (!n || vistos.has(clave(n))) continue;
    vistos.add(clave(n));
    const extra = [tel.tipo, tel.de_quien?.replace(/_/g, " ")]
      .map(limpio)
      .filter(Boolean)
      .join(", ");
    res.push(extra ? `${n} (${extra})` : n);
  }
  const propio = limpio(negocio.telefono);
  if (propio && !vistos.has(clave(propio))) res.unshift(propio);
  return res;
};

const linea = (etiqueta: string, valor?: string | null) =>
  limpio(valor) ? `- ${etiqueta}: ${limpio(valor)}` : null;

export const textoSdr = ({
  negocio,
  notas,
  nombreUsuario,
  t,
}: DatosSdr): string => {
  const ficha = negocio.prospect_ficha as FichaProspect | null | undefined;
  const contacto = contactoSdr(negocio);
  const cargo = limpio(negocio.cargo_contacto) || limpio(ficha?.dueno?.cargo);
  const email = limpio(negocio.email) || limpio(ficha?.email_negocio?.email);
  const tels = telefonos(negocio, ficha);
  const analisis = ficha?.analisis;
  const ordenadas = [...notas].sort((a, b) =>
    String(a.fecha).localeCompare(String(b.fecha)),
  );

  const partes: (string | null)[] = [
    tituloSdr(negocio, t),
    "",
    t("nevora.sdr.intro"),
    `1. ${t("nevora.sdr.tarea_core", { contacto: contacto || t("nevora.sdr.sin_contacto") })}`,
    `2. ${t("nevora.sdr.tarea_sdr")}`,
    "",
    `## ${t("nevora.sdr.consultoria")}`,
    linea(t("nevora.sdr.fecha_hora"), formatoFechaHora(negocio.fecha_reunion)),
    linea(t("nevora.sdr.agendada_por"), nombreUsuario(negocio.agendado_por)),
    "",
    `## ${t("nevora.sdr.negocio")}`,
    linea(t("nevora.sdr.nombre"), negocio.nombre),
    linea(t("nevora.sdr.sector"), negocio.sector || ficha?.sector),
    linea(t("nevora.sdr.municipio"), ficha?.municipio),
    linea(t("nevora.sdr.direccion"), negocio.direccion || ficha?.direccion),
    linea(t("nevora.sdr.web"), negocio.web || ficha?.web),
    linea(t("nevora.sdr.maps"), ficha?.maps_url),
    "",
    `## ${t("nevora.sdr.contacto")}`,
    linea(
      t("nevora.sdr.persona"),
      [contacto, cargo].filter(Boolean).join(" · "),
    ),
    linea(t("nevora.sdr.email"), email),
    tels.length ? `- ${t("nevora.sdr.telefonos")}:` : null,
    ...tels.map((n) => `  - ${n}`),
  ];

  if (ficha) {
    partes.push("", `## ${t("nevora.sdr.prospect")}`);
    partes.push(
      linea(t("nevora.sdr.resumen"), negocio.prospect_resumen || ficha.resumen),
    );
    partes.push(linea(t("nevora.sdr.caso"), ficha.caso));
    if (analisis) {
      partes.push(linea(t("nevora.sdr.analisis"), analisis.resumen));
      if (analisis.sector?.veredicto) {
        partes.push(
          linea(
            t("nevora.sdr.del_sector"),
            [analisis.sector.veredicto, analisis.sector.motivo]
              .filter(Boolean)
              .join(" — "),
          ),
        );
      }
      const senales = (analisis.senales ?? []).filter((s) =>
        limpio(s.etiqueta),
      );
      if (senales.length) {
        partes.push(`- ${t("nevora.sdr.senales")}:`);
        for (const s of senales) {
          partes.push(
            `  - ${[limpio(s.etiqueta), limpio(s.evidencia)].filter(Boolean).join(": ")}${
              limpio(s.fuente_url) ? ` (${limpio(s.fuente_url)})` : ""
            }`,
          );
        }
      }
      const hechos = (analisis.hechos ?? []).filter((h) => limpio(h.hecho));
      if (hechos.length) {
        partes.push(`- ${t("nevora.sdr.hechos")}:`);
        for (const h of hechos) {
          const fuente = [
            limpio(h.fuente_url),
            h.fecha ? formatoFecha(h.fecha) : "",
          ]
            .filter(Boolean)
            .join(", ");
          partes.push(`  - ${limpio(h.hecho)}${fuente ? ` (${fuente})` : ""}`);
        }
      }
    }
  }

  partes.push("", `## ${t("nevora.sdr.notas")}`);
  if (!ordenadas.length) partes.push(`- ${t("nevora.sdr.sin_notas")}`);
  for (const n of ordenadas) {
    const cabecera = [
      formatoFechaHora(n.fecha),
      n.canal === "otro" ? "" : t(claveCanal(n.canal)),
      n.resultado ? t(claveResultadoPorCanal(n.canal, n.resultado)) : "",
      nombreUsuario(n.autor),
    ]
      .filter(Boolean)
      .join(" · ");
    // Las notas automáticas de cambio de etapa no traen texto: se dice qué cambió.
    const cambio =
      !limpio(n.texto) && n.fase_nueva
        ? `${t("nevora.sdr.cambio_etapa")}: ${[n.fase_anterior, n.fase_nueva]
            .filter((f): f is string => !!f)
            .map((f) => t(claveEtapaOFase(f)))
            .join(" → ")}`
        : "";
    const cuerpo = [
      limpio(n.texto) || cambio,
      n.objecion ? `${t("nevora.sdr.objecion")}: ${limpio(n.objecion)}` : "",
    ]
      .filter(Boolean)
      .join(" — ");
    partes.push(`- ${cabecera}${cuerpo ? `: ${cuerpo}` : ""}`);
  }

  return partes
    .filter((p): p is string => p !== null)
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};
